import IconButton from 'components/@extended/IconButton';
import { Close as CloseIcon } from 'components/icons';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import TabsGallery, { TabType } from 'components/TabsGallery';
import useAuth from 'hooks/useAuth.ts';
import CountBox from 'layout/MainLayout/Header/CountBox';
import { dispatch } from 'store';
import { closeTemporaryDrawer } from 'store/reducers/temporaryDrawer';
import { AddressedTaskListItem, TaskTypeCode } from 'types/tasks';
import { t } from 'utils/getTranslations';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import AddressedTasksList from './AddressedTasks/AddressedTasksList';

const isAutomaticComplianceTask = (task: AddressedTaskListItem): boolean => {
    return !!task.isAutomatic && !!task.taskTypeCodes?.some((code) => TaskTypeCode.COMPLIANCE_TASK === code);
};

export interface Props {
    tasks: AddressedTaskListItem[];
    openAddressTaskView: (taskId: number) => void;
    toggleDrawer: () => void;
}

const TasksDrawerContent: React.FC<Props> = ({ tasks, openAddressTaskView, toggleDrawer }) => {
    const { user } = useAuth();
    const userId = user?.id;
    const navigate = useNavigateWithReturnState();

    const openTasksPage = () => {
        toggleDrawer();
        navigate('/tasks');
    };

    const closeDrawer = useCallback(() => {
        dispatch(closeTemporaryDrawer());
    }, []);

    const { automaticComplianceTasks, otherTasks } = tasks.reduce(
        (acc, task) => {
            if (isAutomaticComplianceTask(task)) {
                acc.automaticComplianceTasks.push(task);
            } else {
                acc.otherTasks.push(task);
            }
            return acc;
        },
        { automaticComplianceTasks: [] as AddressedTaskListItem[], otherTasks: [] as AddressedTaskListItem[] }
    );

    const TABS: TabType[] = useMemo(() => {
        return [
            {
                id: 'tasks',
                label: t('tasks.tasks'),
                component: (
                    <AddressedTasksList
                        tasks={otherTasks}
                        userId={userId ?? null}
                        openAddressedTaskView={openAddressTaskView}
                        toggleDrawer={toggleDrawer}
                        filterTasks={(task) => !isAutomaticComplianceTask(task)}
                    />
                ),
                className: 'h-full overflow-auto',
                icon: <CountBox>{otherTasks.length ?? 0}</CountBox>,
            },
            {
                id: 'compliance',
                label: t('compliance.compliance'),
                component: (
                    <AddressedTasksList
                        tasks={automaticComplianceTasks}
                        userId={userId ?? null}
                        openAddressedTaskView={openAddressTaskView}
                        toggleDrawer={toggleDrawer}
                        filterTasks={isAutomaticComplianceTask}
                    />
                ),
                className: 'h-full overflow-auto',
                icon: <CountBox>{automaticComplianceTasks.length ?? 0}</CountBox>,
            },
        ];
    }, [openAddressTaskView, toggleDrawer, userId, automaticComplianceTasks, otherTasks]);

    return (
        <div className="flex flex-col overflow-hidden">
            <div className="flex justify-between px-6 pt-5 pb-1">
                <p className="text-base leading-5.5 font-semibold">
                    <FormattedMessage id="tasks.tasks" />
                </p>
                <div className="flex items-center gap-2">
                    <button type="button" className="text-link ml-3.75 cursor-pointer" onClick={openTasksPage}>
                        <FormattedMessage id="tasks.view-form.all-tasks" />
                    </button>
                    <IconButton
                        aria-label="close tasks"
                        className="text-fg-secondary hover:text-fg"
                        onClick={closeDrawer}
                    >
                        <CloseIcon />
                    </IconButton>
                </div>
            </div>

            <TabsGallery
                wrapperClassName="grid h-full grid-rows-[auto_minmax(0,1fr)] overflow-hidden"
                className="mb-0 px-2"
                tabs={TABS}
            />
        </div>
    );
};

export default TasksDrawerContent;
